import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm } from "../../../hooks";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { Field } from "../miProfile/Field";
import { useDispatch, useSelector } from "react-redux";
import { unSelectPartner } from "../../../store/partner/partnerSlice";
import { editPartner } from "../../helpers/partner";
import { toEnglishDate } from "../../../helpers/dateFormat";
import { DateInput } from "../DateInput";
import { countryList } from "../../../helpers/countryList";
import { CheckboxInput } from "../BooleanInput";

const roleOptions = ["Admin", "Club", "LP", "Híbrido"];
const isActiveOptions = ["Activo", "Baja"];
const typeDniOptions = ["DNI", "Pasaporte", "NIE", "Otro documento"];

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50%",
  maxWidth: 500,
  minWidth: 300,
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  padding: "25px 25px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-around",
  height: "70%",
};

export const EditPartner = ({ setHidden, setReloadData }) => {
  const { userToAction } = useSelector((state) => state.partner);
  const dispatch = useDispatch();
  const booleanIsActiveToString = userToAction.isActive ? "Activo" : "Baja";

  const { t } = useTranslation("global");
  const [formValues, handleInputChange, reset, setValues] = useForm({
    ...userToAction,
    isActive: booleanIsActiveToString,
    registrationDate: toEnglishDate(userToAction.registrationDate),
  });

  const {
    country,
    name,
    surnames,
    email,
    phone,
    address,
    zipCode,
    swift,
    iban,
    role,
    dni,
    typeDni,
    registrationDate,
    club,
    language,
    isActive,
    idCrm,
    urlDocuments,
    investmentType,
    linkedin,
    visibleProfile
  } = formValues;

  const investmentTypeOptions =
    role === "Admin" || role === "LP"
      ? ["N/A"]
      : ["Deal Partner", "Club Partner"];

  const onSubmit = async (e) => {
    e.preventDefault();
    editPartner(
      formValues,
      t("editpartner.succesMsg"),
      t("editpartner.errorMsg"),
      t("editpartner.dupEmailMsg")
    );
    setHidden(false);
    setReloadData((prevState) => !prevState);
  };

  const handleCancel = () => {
    reset();
    dispatch(unSelectPartner());
    setHidden(false);
  };

  const handleRoleChange = (e) => {
    const roleValue = e.target.value;
    setValues((prevValue) => {
      return { ...prevValue, role: roleValue };
    });

    const investmentTypeValue =
      roleValue === "Admin" || roleValue === "LP" ? "N/A" : "Club Partner";
    setValues((prevValue) => {
      return { ...prevValue, investmentType: investmentTypeValue };
    });
  };

  return (
    <>
      <Modal
        open={true}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ zIndex: 9999 }}
      >
        <Box sx={style}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ height: "50px" }}
          >
            <Typography
              color="primary"
              marginBottom="10px"
              variant="h5"
              fontWeight={700}
            >
              {t("myprofile.edituser")}
            </Typography>
            <IconButton color="primary" onClick={handleCancel}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
          <form
            onSubmit={onSubmit}
            style={{
              padding: "10px 5px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-around",
            }}
          >
            <Stack
              sx={{
                height: "calc(70vh - 200px)",
                overflowY: "auto",
                paddingBottom: "10px",
                paddingRight: "20px",
              }}
            >
              <Stack
                direction="row"
                width="100%"
                gap={2}
                alignItems="flex-end"
                sx={{ marginBottom: "15px" }}
              >
                <Stack width="100%" padding="0 5px">
                  <Typography variant="overline" color="secondary">
                    {t("myprofile.role")}
                  </Typography>
                  <Autocomplete
                    disablePortal
                    options={roleOptions}
                    size="small"
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" name="role" />
                    )}
                    value={role}
                    onSelect={handleRoleChange}
                    required
                  />
                </Stack>
              </Stack>
              <Stack
                direction="row"
                width="100%"
                gap={2}
                alignItems="flex-end"
                sx={{ marginBottom: "15px" }}
              ></Stack>

              <Stack
                direction="row"
                width="100%"
                gap={2}
                alignItems="flex-end"
                sx={{ marginBottom: "15px" }}
              >
                <Stack width="100%" padding="0 5px">
                  <Typography variant="overline" color="secondary">
                    {t("myprofile.status")}
                  </Typography>
                  <Autocomplete
                    disablePortal
                    options={isActiveOptions}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        name="isActive"
                      />
                    )}
                    value={isActive}
                    onSelect={handleInputChange}
                    required
                  />
                </Stack>
              </Stack>

              <Stack
                direction="row"
                width="100%"
                gap={2}
                alignItems="flex-end"
                sx={{ marginBottom: "15px" }}
              >
                <Stack width="100%" padding="0 5px">
                  <Typography variant="overline" color="secondary">
                    {t("partnerlist.investmentType")}
                  </Typography>
                  <Autocomplete
                    disablePortal
                    options={investmentTypeOptions}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        name="investmentType"
                      />
                    )}
                    value={investmentType}
                    onSelect={handleInputChange}
                    required
                    disabled={role === "Admin" || role === "LP"}
                  />
                </Stack>
              </Stack>

              <Grid container direction="row" width="100%">
                <Grid item xs={12} padding="0 5px">
                  <Field
                    translation="myprofile"
                    name={urlDocuments}
                    title="urlDocuments"
                    handleInputChange={handleInputChange}
                    required={true}
                  />
                </Grid>
              </Grid>

              <Grid container direction="row" width="100%">
                <Grid item xs={12} md={6} padding="0 5px">
                  <Field
                    translation="myprofile"
                    name={club}
                    title="club"
                    handleInputChange={handleInputChange}
                    required={true}
                  />
                </Grid>
                <Grid item xs={12} md={6} padding="0 5px">
                  <Field
                    translation="partnerlist"
                    name={idCrm}
                    title="idCrm"
                    handleInputChange={handleInputChange}
                    required={true}
                  />
                </Grid>
              </Grid>

              <Grid container direction="row" width="100%">
                <Grid item xs={12} md={6} padding="0 5px">
                  <Field
                    translation="myprofile"
                    name={name}
                    title="name"
                    handleInputChange={handleInputChange}
                    required={true}
                  />
                </Grid>
                <Grid item xs={12} md={6} padding="0 5px">
                  <Field
                    translation="myprofile"
                    name={surnames}
                    title="surnames"
                    handleInputChange={handleInputChange}
                    required={true}
                  />
                </Grid>
              </Grid>

              <Grid container direction="row" width="100%">
                <Grid item xs={12} padding="0 5px">
                  <Typography variant="overline" color="secondary">
                    {t("myprofile.typeDni")}
                  </Typography>
                  <Autocomplete
                    disablePortal
                    options={typeDniOptions}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        name="typeDni"
                      />
                    )}
                    value={typeDni}
                    onSelect={handleInputChange}
                    required
                  />
                </Grid>
              </Grid>

              <Grid container direction="row" width="100%">
                <Grid item xs={12} md={6} padding="0 5px">
                  <Field
                    translation="myprofile"
                    name={dni}
                    title="dni"
                    handleInputChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} md={6} padding="0 5px">
                  <Field
                    translation="myprofile"
                    name={phone}
                    title="phone"
                    handleInputChange={handleInputChange}
                  />
                </Grid>
              </Grid>

              <Grid container direction="row" width="100%">
                <Grid item xs={12} padding="0 5px">
                  <Field
                    translation="myprofile"
                    name={email}
                    title="email"
                    handleInputChange={handleInputChange}
                    required={true}
                  />
                </Grid>
              </Grid>

              <Grid container direction="row" width="100%">
                <Grid item xs={12} md={6} padding="0 5px">
                  <DateInput
                    name={registrationDate}
                    title="registrationDate"
                    handleInputChange={handleInputChange}
                    required={true}
                  />
                </Grid>
                <Grid item xs={12} md={6} padding="0 5px">
                  <Field
                    translation="myprofile"
                    name={language}
                    title="language"
                    handleInputChange={handleInputChange}
                  />
                </Grid>
              </Grid>

              <Grid container direction="row" width="100%">
                <Grid item xs={12} padding="0 5px">
                  <Field
                    translation="myprofile"
                    name={address}
                    title="address"
                    handleInputChange={handleInputChange}
                  />
                </Grid>
              </Grid>

              <Grid container direction="row" width="100%">
                <Grid item xs={12} padding="0 5px">
                  <Typography variant="overline" color="secondary">
                    {t("myprofile.country")}
                  </Typography>
                  <Autocomplete
                    disablePortal
                    options={countryList}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        name="country"
                      />
                    )}
                    value={country}
                    onSelect={handleInputChange}
                    required
                  />
                </Grid>
              </Grid>

              <Grid container direction="row" width="100%">
                <Grid item xs={12} padding="0 5px">
                  <Field
                    translation="myprofile"
                    name={zipCode}
                    title="zipCode"
                    handleInputChange={handleInputChange}
                  />
                </Grid>
              </Grid>

              <Grid container direction="row" width="100%">
                <Grid item xs={12} md={6} padding="0 5px">
                  <Field
                    translation="myprofile"
                    name={swift}
                    title="swift"
                    handleInputChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} md={6} padding="0 5px">
                  <Field
                    translation="myprofile"
                    name={iban}
                    title="iban"
                    handleInputChange={handleInputChange}
                  />
                </Grid>
              </Grid>
              <Grid container direction="row" width="100%">
                <Grid item xs={12}>
                  <Field
                    translation="myprofile"
                    name={linkedin}
                    title="linkedin"
                    handleInputChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CheckboxInput 
                    translation="myprofile"
                    value={visibleProfile}
                    title="visibleProfile"
                    handleInputChange={handleInputChange}
                  />
                </Grid>
              </Grid>
            </Stack>

            <Stack sx={{ heigth: "80px" }}>
              <Divider sx={{ margin: "30px 0px 15px 0px" }} />

              <Grid container direction="row" width="100%">
                <Grid item xs={12} md={6} padding="0 5px">
                  <Button fullWidth onClick={handleCancel} variant="text">
                    {t("createPassword.cancelButton")}
                  </Button>
                </Grid>
                <Grid item xs={12} md={6} padding="0 5px">
                  <Button fullWidth type="submit" variant="contained">
                    {t("createPassword.principalButton")}
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          </form>
        </Box>
      </Modal>
    </>
  );
};
